import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import { AssignmentIcon, PeopleIcon } from '@orijinworks/frontend-commons';
import { LOCATIONS } from '../../../constants';
import {
  CASE_PLAN_CREATE_PERMISSIONS,
  REENTRY_PLANNING_TAB_CONFIG,
  REENTRY_PLANNING_TABS,
} from './constants/reentry-planning.constants';
import { useLocationsRevision } from './hooks/useLocationsRevision';
import { isAnyPermitted } from './utils/case-plan-permission.util';
import {
  resolveActiveReentryPlanningTabLabel,
  resolveReentryPlanningTab,
  resolveVisibleReentryPlanningTabs,
} from './utils/reentry-plans-scope.util';
import {
  canAccessReentryPlanning,
  canListReentryPlans,
  canShowMyPlansTab,
  hasCasePlanAssignedDetailAccess,
  resolveEntitledPlanTypesFromLocations,
  shouldShowCreatePlanButton,
} from './utils/reentry-planning.util';

export const ACCESS_STATUS = {
  PENDING: 'pending',
  GRANTED: 'granted',
  DENIED: 'denied',
};

/**
 * Icons must come from fe-commons: FolderNav colours the inactive tab icon with
 * `theme.palette.navy.main`, which only exists on the fe-commons theme. Icons imported from the
 * app's own `@mui/icons-material` resolve the app's MUI theme instead and crash on the missing key.
 */
const TAB_ICONS = {
  [REENTRY_PLANNING_TABS.MY_PLANS]: AssignmentIcon,
  [REENTRY_PLANNING_TABS.ALL_PLANS]: PeopleIcon,
};

const REENTRY_PLANNING_FOLDER_TABS = REENTRY_PLANNING_TAB_CONFIG.map(({ label }) => ({
  label,
  value: label,
  Icon: TAB_ICONS[label],
}));

const [DEFAULT_TAB] = REENTRY_PLANNING_FOLDER_TABS;

export const useReentryPlanningModuleHook = () => {
  const [global] = useGlobal();
  const [accessStatus, setAccessStatus] = useState(ACCESS_STATUS.PENDING);
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB.value);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const locationsRevision = useLocationsRevision();

  const { cbacConfigFlag, permissions, permissionIds, isSuperAdmin } = global;

  const entitledPlanTypes = useMemo(
    () => resolveEntitledPlanTypesFromLocations(LOCATIONS),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationsRevision],
  );

  // Strict grant check: CBAC-off `isPermitted` would unlock My Plans for preview-only staff.
  const canViewMyPlans = useMemo(
    () =>
      canShowMyPlansTab({
        hasAssignedDetailPermission: hasCasePlanAssignedDetailAccess(permissions, permissionIds),
        locations: LOCATIONS,
      }),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationsRevision, permissionIds, permissions],
  );

  const canViewAllPlansList = useMemo(
    () => canListReentryPlans({ locations: LOCATIONS, permissions, permissionIds }),
    // LOCATIONS is module-level; `locationsRevision` bumps when it is populated (LOCATIONS_FETCHED).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationsRevision, permissionIds, permissions],
  );

  const tabs = useMemo(
    () =>
      resolveVisibleReentryPlanningTabs({
        canViewAllPlansList,
        canViewMyPlans,
        folderTabs: REENTRY_PLANNING_FOLDER_TABS,
      }),
    [canViewAllPlansList, canViewMyPlans],
  );

  const resolvedActiveTab = useMemo(
    () => resolveActiveReentryPlanningTabLabel(activeTab, tabs),
    [activeTab, tabs],
  );

  const activeTabConfig = useMemo(
    () => resolveReentryPlanningTab(resolvedActiveTab),
    [resolvedActiveTab],
  );

  const showCreatePlanButton =
    shouldShowCreatePlanButton(entitledPlanTypes) &&
    isAnyPermitted(cbacConfigFlag, permissions, CASE_PLAN_CREATE_PERMISSIONS, permissionIds);

  const checkAccess = useCallback(() => {
    const granted = canAccessReentryPlanning(permissions, permissionIds, {
      isSuperAdmin,
      locations: LOCATIONS,
    });

    setAccessStatus(granted ? ACCESS_STATUS.GRANTED : ACCESS_STATUS.DENIED);
  }, [permissions, permissionIds, isSuperAdmin]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const onTabChange = useCallback((_event, value) => {
    setActiveTab(value);
  }, []);

  const refreshPlans = useCallback(() => {
    setLastRefreshed(new Date());
  }, []);

  return {
    accessStatus,
    activeTab: resolvedActiveTab,
    activeTabConfig,
    entitledPlanTypes,
    lastRefreshed,
    onTabChange,
    refreshPlans,
    tabs,
    showCreatePlanButton,
  };
};
